/**
 * Extension host — activates discovered `ExtensionUI` modules, builds their
 * `ExtensionActivationContext`, collects dynamic contributions, and bridges
 * `callRuntime` / `onRuntimeEvent` to the Engine runtime via an injected
 * `RuntimeBridge`. Mirrors the plugin lifecycle but for editor-shell extensions.
 *
 * Lifecycle:
 *   discover → registerExtensionUI → activate(ctx) registers contributions
 *   → callRuntime ensures runtime activated → broadcast → onRuntimeEvent
 *   → settings change → onSettingsChanged / runtime.updateSettings
 *   → deactivate → dispose contributions + runtime.dispose
 */
import type {
    AnyExtensionContract,
    Disposable,
    ExtensionActivationContext,
    ExtensionEventName,
    ExtensionEventPayload,
    ExtensionMethodName,
    ExtensionMethodParams,
    ExtensionMethodResult,
    ExtensionUI,
    LocaleParams,
} from "@magic-md/extension-sdk";
import {
    addCommand,
    addFloatingPanel,
    addMenuProvider,
    addSettingsPanel,
    addSidebarPanel,
    addStatusBarItem,
    addUnitConfigAction,
    addVirtualPackageProvider,
    removeAllForExtension,
} from "./contribution-registry";
import { extensionUIRegistry } from "./extension-ui-registry";
import { useExtensionHostStore } from "./extension-host-store";
import type { HostServices } from "./host-services";

interface ActiveExtension {
    id: string;
    disposers: Array<() => void>;
    exports?: unknown;
}

const active = new Map<string, ActiveExtension>();

function createFloatingPanelInstanceId(extensionId: string, panelId: string): string {
    return `${extensionId}:${panelId}:${Date.now().toString(36)}:${Math.random()
        .toString(36)
        .slice(2, 7)}`;
}

function asDisposable(dispose: () => void): Disposable {
    return { dispose };
}

function buildContext(
    ext: ExtensionUI,
    services: HostServices,
    activeEntry: ActiveExtension,
): ExtensionActivationContext<AnyExtensionContract> {
    const extensionId = ext.manifest.id;
    const namespace = ext.i18n?.namespace ?? extensionId;
    const track = (dispose: () => void): Disposable => {
        activeEntry.disposers.push(dispose);
        return asDisposable(dispose);
    };

    return {
        extensionId,
        settings: services.settings.get(extensionId),
        onSettingsChanged: (cb) => track(services.settings.onChanged(extensionId, cb)),

        registerSidebarPanel: (opts) => track(addSidebarPanel(extensionId, opts)),
        registerFloatingPanel: (opts) => track(addFloatingPanel(extensionId, opts)),
        registerCommand: (opts) => track(addCommand(extensionId, opts)),
        registerStatusBarItem: (opts) => track(addStatusBarItem(extensionId, opts)),
        registerSettingsPanel: (component) => track(addSettingsPanel(extensionId, component)),
        openFloatingPanel: (panelId, options) => {
            const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
            const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
            const width = options?.width ?? 460;
            const height = options?.height ?? 520;
            const instanceId = createFloatingPanelInstanceId(extensionId, panelId);
            const panel = {
                instanceId,
                extensionId,
                panelId,
                title: options?.title,
                payload: options?.payload,
                x:
                    options?.x ??
                    Math.max(56, Math.round((viewportWidth - width) / 2)),
                y:
                    options?.y ??
                    Math.max(48, Math.round((viewportHeight - height) / 3)),
                width,
                height,
            };
            return useExtensionHostStore.getState().openFloatingPanel({
                ...panel,
            });
        },
        closeFloatingPanel: (instanceId) => {
            useExtensionHostStore.getState().closeFloatingPanel(instanceId);
        },
        registerMenuProvider: (provider) => track(addMenuProvider(extensionId, provider)),
        registerUnitConfigAction: (action) => track(addUnitConfigAction(extensionId, action)),
        registerVirtualPackageProvider: (provider) =>
            track(addVirtualPackageProvider(extensionId, provider)),

        callRuntime: async <K extends ExtensionMethodName<AnyExtensionContract>>(
            method: K,
            params: ExtensionMethodParams<AnyExtensionContract, K>,
        ): Promise<ExtensionMethodResult<AnyExtensionContract, K>> => {
            await services.runtime.ensureActivated(extensionId, services.settings.get(extensionId));
            return services.runtime.invoke<AnyExtensionContract, K>(extensionId, method, params);
        },
        onRuntimeEvent: <K extends ExtensionEventName<AnyExtensionContract>>(
            event: K,
            handler: (data: ExtensionEventPayload<AnyExtensionContract, K>) => void,
        ): (() => void) => {
            const off = services.runtime.subscribe<AnyExtensionContract, K>(
                extensionId,
                event,
                handler,
            );
            activeEntry.disposers.push(off);
            return off;
        },

        getExtension: <A = unknown>(id: string) => {
            const api = services.getExtensionExports?.<A>(id);
            return api === undefined ? undefined : { id, api };
        },

        host: services.host,

        t: (key: string, params?: LocaleParams) =>
            services.i18n.translate(namespace, key, params),
        locale: services.i18n.locale,
        onLocaleChanged: (cb) => track(services.i18n.onLocaleChanged(cb)),

        log: (level, msg) => services.log(extensionId, level, msg),
    };
}

/**
 * Activate one discovered extension. Idempotent — re-activating a live
 * extension returns its existing entry.
 */
export async function activateExtension(
    extensionId: string,
    services: HostServices,
): Promise<{ activated: boolean; exports?: unknown }> {
    if (active.has(extensionId)) {
        return { activated: true, exports: active.get(extensionId)?.exports };
    }
    const ext = extensionUIRegistry.getRaw(extensionId);
    if (!ext) throw new Error(`Extension not found: ${extensionId}`);

    const entry: ActiveExtension = { id: extensionId, disposers: [] };
    active.set(extensionId, entry);

    const ctx = buildContext(ext, services, entry);
    try {
        const result = await ext.activate(ctx);
        if (typeof result === "function") {
            entry.disposers.push(result as () => void);
        } else if (result && typeof result === "object" && "dispose" in result) {
            entry.disposers.push(() => (result as Disposable).dispose());
        } else if (result && typeof result === "object") {
            entry.exports = result;
        }
        return { activated: true, exports: entry.exports };
    } catch (err) {
        await deactivateExtension(extensionId, services);
        throw err;
    }
}

/** Deactivate one extension: dispose contributions + runtime. */
export async function deactivateExtension(
    extensionId: string,
    services: HostServices,
): Promise<void> {
    const entry = active.get(extensionId);
    if (!entry) return;
    for (const dispose of entry.disposers.splice(0).reverse()) {
        try {
            dispose();
        } catch {
            // best-effort cleanup
        }
    }
    removeAllForExtension(extensionId);
    active.delete(extensionId);
    try {
        await services.runtime.dispose(extensionId);
    } catch {
        // runtime may not have been activated
    }
}

/** Public exports of an active extension, if any. */
export function getActiveExtensionExports<A = unknown>(extensionId: string): A | undefined {
    return active.get(extensionId)?.exports as A | undefined;
}

export function isExtensionActive(extensionId: string): boolean {
    return active.has(extensionId);
}

export function activeExtensionIds(): string[] {
    return [...active.keys()];
}

/** Deactivate everything (e.g. switching engine modes / closing workspace). */
export async function deactivateAllExtensions(services: HostServices): Promise<void> {
    for (const id of [...active.keys()]) {
        await deactivateExtension(id, services);
    }
}
